import type {
  ExamImage,
  ProcedureExamType,
  ProcedureFindingValue,
  ProcedureWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { otherTextKey } from "../worksheet-field-defs";
import { GUIDED_JOINT_INJECTION } from "./guided-joint-injection";
import { GUIDED_NERVE_BLOCK } from "./guided-nerve-block";
import { GUIDED_TENDON_INJECTION } from "./guided-tendon-injection";
import type {
  ProcedureDefinition,
  ProcedureFieldDef,
} from "./procedure-fields";

export const PROCEDURE_DEFINITIONS: Record<
  ProcedureExamType,
  ProcedureDefinition
> = {
  guided_joint_injection: GUIDED_JOINT_INJECTION,
  guided_tendon_injection: GUIDED_TENDON_INJECTION,
  guided_nerve_block: GUIDED_NERVE_BLOCK,
};

const isAnswered = (value: ProcedureFindingValue | undefined): boolean =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined && value !== "";

export const isProcedureFieldShown = (
  findings: ProcedureWorksheetData["findings"],
  field: ProcedureFieldDef,
): boolean => {
  if (!field.showWhen) return true;
  const value = findings[field.showWhen.field];
  return typeof value === "string" && field.showWhen.in.includes(value);
};

const revealsText = (field: ProcedureFieldDef, value: string): boolean =>
  (field.kind === "single" || field.kind === "multi") &&
  field.options.some((option) => option.value === value && option.revealsText);

const selectedRevealsText = (
  field: ProcedureFieldDef,
  value: ProcedureFindingValue | undefined,
): boolean =>
  Array.isArray(value)
    ? value.some((item) => revealsText(field, item))
    : typeof value === "string" && revealsText(field, value);

export const normalizeProcedure = (
  data: ProcedureWorksheetData,
): ProcedureWorksheetData => {
  const definition = PROCEDURE_DEFINITIONS[data.examType];
  const findings = Object.fromEntries(
    definition.fields.flatMap((field) => {
      if (!isProcedureFieldShown(data.findings, field)) return [];
      const value = data.findings[field.id];
      const entries: [string, ProcedureFindingValue][] = [];
      if (value !== undefined) entries.push([field.id, value]);
      const other = data.findings[otherTextKey(field.id)];
      if (other !== undefined && selectedRevealsText(field, value)) {
        entries.push([otherTextKey(field.id), other]);
      }
      return entries;
    }),
  );
  return { ...data, definitionVersion: definition.version, findings };
};

export const isProcedureComplete = (
  data: ProcedureWorksheetData,
  images: readonly ExamImage[],
): boolean => {
  const definition = PROCEDURE_DEFINITIONS[data.examType];
  const needleImageRequired =
    data.findings.needle_in_target_image_saved === "yes";
  return (
    data.indication.length > 0 &&
    definition.completeWhenAnswered.every((id) =>
      isAnswered(data.findings[id]),
    ) &&
    images.length > 0 &&
    (!needleImageRequired || images.some((image) => image.needleInTarget))
  );
};

export const procedureOptionLabel = (
  data: ProcedureWorksheetData,
  fieldId: string,
): string | null => {
  const field = PROCEDURE_DEFINITIONS[data.examType].fields.find(
    (candidate) => candidate.id === fieldId,
  );
  const value = data.findings[fieldId];
  if (!field || typeof value !== "string") return null;
  if (field.kind !== "single") return value;
  const other = data.findings[otherTextKey(fieldId)];
  if (revealsText(field, value) && typeof other === "string" && other) {
    return other;
  }
  return field.options.find((option) => option.value === value)?.label ?? value;
};

export const procedureIndicationLabels = (
  data: ProcedureWorksheetData,
): string[] => {
  const { indications } = PROCEDURE_DEFINITIONS[data.examType];
  return data.indication.map(
    (code) =>
      indications.find((indication) => indication.value === code)?.label ??
      code,
  );
};

export const procedureViewLabels = (data: ProcedureWorksheetData): string[] =>
  PROCEDURE_DEFINITIONS[data.examType].views
    .filter((view) => data.views[view.value])
    .map((view) => view.label);

export const procedureCodesFor = (
  data: ProcedureWorksheetData,
): readonly string[] => {
  const definition = PROCEDURE_DEFINITIONS[data.examType];
  const site = data.findings[definition.siteFieldId];
  if (typeof site !== "string") return [];
  const fromField = definition.codesFromField;
  if (fromField && site === fromField.whenSite) {
    const answer = data.findings[fromField.fieldId];
    return typeof answer === "string" ? (fromField.codes[answer] ?? []) : [];
  }
  return definition.codesBySite[site] ?? [];
};

const routineAnswers = (data: ProcedureWorksheetData): [string, string][] =>
  PROCEDURE_DEFINITIONS[data.examType].fields.flatMap((field) =>
    field.kind === "single" && field.normal !== null
      ? [[field.id, field.normal]]
      : [],
  );

export const isRoutineProcedure = (data: ProcedureWorksheetData): boolean =>
  routineAnswers(data).every(([id, value]) => data.findings[id] === value);

export const toggleRoutineProcedure = (
  data: ProcedureWorksheetData,
): ProcedureWorksheetData => {
  const routine = isRoutineProcedure(data);
  return {
    ...data,
    findings: {
      ...data.findings,
      ...Object.fromEntries(
        routineAnswers(data).map(([id, value]) => [id, routine ? null : value]),
      ),
    },
  };
};
